import { WebsiteEditorSplitLayout } from '@/components/forms/DesignForm/WebsiteEditorSplitLayout'
import { WEBSITE_EDITOR_TARGET_SECTION_KEY } from '@/components/forms/DesignForm/websiteEditorTargetSection'
import { AdminHeaderActionButton, IconActionButton } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { Link } from '@tanstack/react-router'
import { useCopyToClipboard } from '@uidotdev/usehooks'
import { Copy, ExternalLink, Info } from 'lucide-react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'

export const CUSTOM_PAGES_DESCRIPTION =
  'Extra pages on your website for things like events, classes, policies and FAQs.'

/** A page's screen fills the window like the Website Builder's, so its preview has room. */
export const CUSTOM_PAGE_SHELL_CLASS_NAME =
  'pb-0 xl:grid xl:h-[calc(100dvh-5.5rem)] xl:min-h-0 xl:grid-rows-[auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden'

/** The Website Builder's workspace: a narrow column of details beside the page itself. */
export function CustomPageWorkspace({ details, panel }: { details: ReactNode; panel: ReactNode }) {
  return (
    <WebsiteEditorSplitLayout
      autoSaveId="custom-page-workspace"
      defaultSettingsSize={30}
      mobileLayout="natural"
      settingsPanel={
        <div className="h-full min-h-0 min-w-0 xl:overflow-hidden">
          <ScrollArea className="h-auto xl:h-full">
            <div className="space-y-4 pr-1 pb-3">{details}</div>
          </ScrollArea>
        </div>
      }
      previewPanel={panel}
    />
  )
}

export function CustomPageStatusBadge({ isPublished }: { isPublished: boolean }) {
  return (
    <Badge variant={isPublished ? 'success' : 'mutedSoft'}>
      {isPublished ? 'Published' : 'Hidden'}
    </Badge>
  )
}

/** The page's address on the website, which florists paste into a menu Link item. */
export function CustomPageAddress({ path }: { path: string }) {
  const [, copyToClipboard] = useCopyToClipboard()

  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <Typography as="span" variant="inlineCode" translate="no" className="truncate">
        {path}
      </Typography>
      <IconActionButton
        icon={Copy}
        label={`Copy page address ${path}`}
        variant="ghost"
        onClick={async () => {
          await copyToClipboard(path)
          toast.success('Page address copied')
        }}
      />
    </span>
  )
}

export function formatCustomPageUpdatedAt(updatedAt: string | null): string {
  return updatedAt ? dayjs(updatedAt).format('MMM D, YYYY [at] h:mm A') : 'Not saved yet'
}

/**
 * For florists, who see pages but don't change them: how to put a published page in their menu.
 * Staff who build pages don't need it, and only people who can edit the website can follow it.
 */
export function CustomPageMenuNotice({ title, changeText }: { title: string; changeText: string }) {
  const path = usePathHelper()
  const orgId = useRouteOrganizationId()

  return (
    <Can I="edit" a="designs">
      <InfoNotice compact icon={<Info />} title={title}>
        <p>
          In the{' '}
          <Typography as="span" tone="interaction" interaction="link">
            <Link
              {...path.builder.index().using({ orgId }).search({ section: 'brand-layout' }).link}
              // The builder opens the section named here: Header & Navigation, where menus are edited.
              onClick={() =>
                window.sessionStorage.setItem(WEBSITE_EDITOR_TARGET_SECTION_KEY, 'header')
              }
            >
              Website Builder
            </Link>
          </Typography>
          , choose Add menu item, pick Link, and paste the page's address. {changeText}
        </p>
      </InfoNotice>
    </Can>
  )
}

/** Opens a published page on the shop's primary domain; nothing shows until the shop has one. */
export function ViewOnWebsiteButton({ path }: { path: string }) {
  const { organization } = useOrganizationContext()
  const domain = organization?.primary_domain?.trim()

  if (!domain) {
    return null
  }

  return (
    <AdminHeaderActionButton variant="outline" asChild>
      <a href={`https://${domain}${path}`} target="_blank" rel="noopener noreferrer">
        View on website
        <ExternalLink className="size-4" aria-hidden="true" />
      </a>
    </AdminHeaderActionButton>
  )
}
